import { Button, Col, DatePicker, Form, Input, Row, Tabs } from 'antd'
import { useTranslation } from 'react-i18next'
import { useForm } from 'antd/lib/form/Form'
import _ from 'lodash'
import dayjs from 'dayjs'
import { CloseOutlined, PlusCircleOutlined } from '@ant-design/icons'
import { useEffect, useState } from 'react'
import { EntKurultaiTask } from '../../../models/kurultai/kurultaiItem'
import { EntIndicatorItem } from '../../../models/kurultai/indicatorItem'
import SelectExecutionStatus from '../../../components/selects/SelectExecutionStatus'
import { dateFormat } from '../../../utils'
import { Assignment } from '../../../models/kurultai/kutultaiRegister'
import SelectQuestionType from '../../../components/selects/SelectQuestionType'

interface IProps {
	setItems: (items: Assignment[]) => void
	items: Assignment[]
}

export const AddKurultaiItems = (props: IProps) => {
	const { t } = useTranslation()
	const [form] = useForm()

	const generateUniqueKey = (): string => crypto.randomUUID()

	const [kurultaiAssignments, setKurultaiAssignments] = useState<Assignment[]>([
		{
			key: generateUniqueKey(),
			id: null,
			questionCategoryId: null,
			question: null,
			questionDescription: null,
			sourceOfFunding: null,
			tasksList: [
				{
					key: generateUniqueKey(),
					id: null,
					description: null,
					assignmentId: null,
					eventsList: [
						{
							key: generateUniqueKey(),
							id: null,
							// description: null,
							indicator: null,
							deadlineDate: null,
							kurultaiStatusId: null,
							responsibleAuthority: null,
							eventDetails: null,
						},
					],
				},
			],
		},
	])

	const [activeKey, setActiveKey] = useState<string | undefined>(kurultaiAssignments[0]?.key)

	useEffect(() => {
		if (!_.isEqual(props.items, kurultaiAssignments)) {
			const itemsCloned = _.cloneDeep(props.items)

			// Если нужно проставлять key заново, пробегаемся и присваиваем:
			itemsCloned.forEach(item => {
				item.key = item.key || generateUniqueKey()
				if (item.tasksList) {
					item.tasksList.forEach(task => {
						task.key = task.key || generateUniqueKey()
						if (task.eventsList) {
							task.eventsList.forEach(event => {
								event.key = event.key || generateUniqueKey()
							})
						}
					})
				}
			})

			setKurultaiAssignments(itemsCloned)
			props.setItems(itemsCloned)
			if (itemsCloned.length > 0 && !itemsCloned.find(item => item.key === activeKey)) {
				setActiveKey(itemsCloned[0].key)
			} else if (itemsCloned.length === 0) {
				setActiveKey(undefined)
			}
		}
	}, [props.items])

	// useEffect(() => {
	// 	if (kurultaiAssignments.length > 0) {
	// 		setActiveKey(kurultaiAssignments[0].key)
	// 	}
	// }, [kurultaiAssignments])

	const isFieldEmpty = (value: any) => {
		return value === null || value === undefined || value === ''
	}

	const handleDeleteItem = (itemKey: string) => {
		setKurultaiAssignments(prevItems => {
			const newItems = prevItems.filter(item => item.key !== itemKey)
			if (newItems.length === 0) {
				props.setItems(newItems)
				return newItems
			}
			if (activeKey === itemKey) {
				setActiveKey(newItems[0]?.key || '')
			}
			props.setItems(newItems)
			return newItems
		})
	}

	const handleInputChange = (key: string, field: keyof Assignment, value: any) => {
		setKurultaiAssignments(prevItems => {
			const updatedItems = prevItems.map(item =>
				item.key === key ? { ...item, [field]: value, tasksList: item.tasksList || [] } : item
			)
			props.setItems(updatedItems)
			return updatedItems
		})
	}

	const handleTaskChange = (
		itemKey: string,
		taskKey: string,
		field: keyof EntKurultaiTask,
		value: any
	) => {
		setKurultaiAssignments(prevItems => {
			const updatedItems = prevItems.map(item =>
				item.key === itemKey
					? {
							...item,
							tasksList: (item.tasksList || []).map(task =>
								task.key === taskKey
									? { ...task, [field]: value, eventsList: task.eventsList || [] }
									: task
							),
					  }
					: item
			)
			props.setItems(updatedItems)
			return updatedItems
		})
	}

	const handleAddItem = () => {
		const newItem: Assignment = {
			key: generateUniqueKey(),
			id: null,
			questionCategoryId: null,
			question: null,
			questionDescription: null,
			sourceOfFunding: null,
			tasksList: [
				{
					key: generateUniqueKey(),
					id: null,
					description: null,
					assignmentId: null,
					eventsList: [
						{
							key: generateUniqueKey(),
							id: null,
							// description: null,
							eventDetails: null,
							indicator: null,
							deadlineDate: null,
							kurultaiStatusId: null,
							responsibleAuthority: null,
						},
					],
				},
			],
		}
		setKurultaiAssignments(prevItems => {
			const updatedItems = [...prevItems, newItem]
			props.setItems(updatedItems)
			setActiveKey(newItem.key || '')
			return updatedItems
		})
	}

	const handleAddTask = (itemKey: string) => {
		setKurultaiAssignments(prevItems => {
			const updatedItems = prevItems.map(item =>
				item.key === itemKey
					? {
							...item,
							tasksList: [
								...(item.tasksList || []),
								{
									key: generateUniqueKey(),
									id: null,
									description: null,
									assignmentId: null,
									eventsList: [
										{
											key: generateUniqueKey(),
											id: null,
											// description: null,
											indicator: null,
											deadlineDate: null,
											kurultaiStatusId: null,
											responsibleAuthority: null,
											eventDetails: null,
										},
									],
								},
							],
					  }
					: item
			)
			props.setItems(updatedItems)
			return updatedItems
		})
	}

	const handleDeleteTask = (itemKey: string, taskKey: string) => {
		setKurultaiAssignments(prevItems => {
			const updatedItems = prevItems.map(item =>
				item.key === itemKey
					? { ...item, tasksList: (item.tasksList || []).filter(task => task.key !== taskKey) }
					: item
			)
			props.setItems(updatedItems)
			return updatedItems
		})
	}

	const handleAddIndicator = (itemKey: string, taskKey: string) => {
		setKurultaiAssignments(prevItems => {
			const updatedItems = prevItems.map(item =>
				item.key === itemKey
					? {
							...item,
							tasksList: (item.tasksList || []).map(task =>
								task.key === taskKey
									? {
											...task,
											eventsList: [
												...(task.eventsList || []),
												{
													key: generateUniqueKey(),
													id: null,
													// description: null,
													indicator: null,
													deadlineDate: null,
													kurultaiStatusId: null,
													responsibleAuthority: null,
													eventDetails: null,
												},
											],
									  }
									: task
							),
					  }
					: item
			)
			props.setItems(updatedItems)
			return updatedItems
		})
	}

	const handleDeleteIndicator = (itemKey: string, taskKey: string, indicatorKey: string) => {
		setKurultaiAssignments(prevItems => {
			const updatedItems = prevItems.map(item =>
				item.key === itemKey
					? {
							...item,
							tasksList: (item.tasksList || []).map(task =>
								task.key === taskKey
									? {
											...task,
											eventsList: (task.eventsList || []).filter(ind => ind.key !== indicatorKey),
									  }
									: task
							),
					  }
					: item
			)
			props.setItems(updatedItems)
			return updatedItems
		})
	}

	const handleIndicatorChange = (
		itemKey: string,
		taskKey: string,
		indicatorKey: string,
		field: keyof EntIndicatorItem,
		value: any
	) => {
		setKurultaiAssignments(prevItems => {
			const updatedItems = prevItems.map(item =>
				item.key === itemKey
					? {
							...item,
							tasksList: (item.tasksList || []).map(task =>
								task.key === taskKey
									? {
											...task,
											eventsList: (task.eventsList || []).map(indicator =>
												indicator.key === indicatorKey
													? {
															...indicator,
															[field]:
																field === 'deadlineDate' && value
																	? dayjs(value).format('YYYY-MM-DD')
																	: value,
													  }
													: indicator
											),
									  }
									: task
							),
					  }
					: item
			)
			props.setItems(updatedItems)
			return updatedItems
		})
	}

	return (
		<div style={{ marginTop: 15 }}>
			<Tabs
				type='editable-card'
				activeKey={activeKey}
				onChange={setActiveKey}
				// defaultActiveKey={kurultaiAssignments[0]?.key}
				onEdit={(key, action) => {
					if (action === 'add') {
						handleAddItem()
					} else if (action === 'remove') {
						handleDeleteItem(key as string)
					}
				}}
			>
				{kurultaiAssignments.map((item, index) => (
					<Tabs.TabPane
						key={item.key}
						tab={`Поручение ${index + 1}`}
						closable={kurultaiAssignments.length > 1}
					>
						<div className='block_item_form massive_blocks'>
							<Row style={{ margin: 0 }} gutter={16}>
								<Col span={24}>
									<label>{t('kurultai.questionCategory')}</label>
									<p>
										<SelectQuestionType
											multiple={false}
											value={item.questionCategoryId}
											onChange={value =>
												handleInputChange(item.key as string, 'questionCategoryId', value)
											}
											status={isFieldEmpty(item.questionCategoryId) ? 'error' : ''}
										/>
									</p>
								</Col>
								<Col span={24}>
									<label>{t('kurultai.question')}</label>
									<Input
										type='text'
										value={item.question || ''}
										onChange={e =>
											handleInputChange(item.key as string, 'question', e.target.value)
										}
										className={isFieldEmpty(item.question) ? 'error-border' : ''}
									/>
									<label>{t('kurultai.questionDescription')}</label>
									<Input
										type='text'
										value={item.questionDescription || ''}
										onChange={e =>
											handleInputChange(item.key as string, 'questionDescription', e.target.value)
										}
										className={isFieldEmpty(item.questionDescription) ? 'error-border' : ''}
									/>
									<label>{t('kurultai.sourceOfFunding')}</label>
									<Input
										type='text'
										value={item.sourceOfFunding || ''}
										onChange={e =>
											handleInputChange(item.key as string, 'sourceOfFunding', e.target.value)
										}
										className={isFieldEmpty(item.sourceOfFunding) ? 'error-border' : ''}
									/>
								</Col>
								<Col span={24}>
									{item.tasksList?.map(task => (
										<div key={task.key} className='block_item_form list_in_linst'>
											<Row style={{ margin: 0 }} gutter={16}>
												<Col span={12}>
													<label>{t('kurultai.taskDescription')}</label>
													<Input
														type='text'
														value={task.description || ''}
														onChange={e =>
															handleTaskChange(
																item.key as string,
																task.key as string,
																'description',
																e.target.value
															)
														}
														className={isFieldEmpty(task.description) ? 'error-border' : ''}
													/>
												</Col>
												<Button
													className='fixed_delete'
													type='primary'
													title='Удалить'
													danger
													icon={<CloseOutlined />}
													shape='circle'
													size='small'
													onClick={() => handleDeleteTask(item.key as string, task.key as string)}
													disabled={(item.tasksList || []).length <= 1}
												/>
												<div className='block_item_form massive_blocks' style={{ width: '100%' }}>
													<Row style={{ margin: 0 }} gutter={16}>
														<Col span={24}>
															{task.eventsList?.map(indicator => (
																<div key={indicator.key} className='block_item_form list_in_linst'>
																	<Row style={{ margin: 0 }} gutter={16}>
																		<Col span={12}>
																			<label>{t('kurultai.indicator')}</label>
																			<Input
																				type='text'
																				value={indicator.indicator || ''}
																				onChange={e =>
																					handleIndicatorChange(
																						item.key as string,
																						task.key as string,
																						indicator.key as string,
																						'indicator',
																						e.target.value
																					)
																				}
																				className={
																					isFieldEmpty(indicator.indicator) ? 'error-border' : ''
																				}
																			/>
																		</Col>
																		<Col span={12}>
																			<label>{t('kurultai.executionStatus')}</label>
																			<p>
																				<SelectExecutionStatus
																					multiple={false}
																					value={indicator.kurultaiStatusId}
																					onChange={value =>
																						handleIndicatorChange(
																							item.key as string,
																							task.key as string,
																							indicator.key as string,
																							'kurultaiStatusId',
																							value
																						)
																					}
																					status={
																						isFieldEmpty(indicator.kurultaiStatusId) ? 'error' : ''
																					}
																				/>
																			</p>
																		</Col>
																		<Col span={12}>
																			<label>{t('kurultai.responsibleAuthority')}</label>
																			<Input
																				type='text'
																				value={indicator.responsibleAuthority || ''}
																				onChange={e =>
																					handleIndicatorChange(
																						item.key as string,
																						task.key as string,
																						indicator.key as string,
																						'responsibleAuthority',
																						e.target.value
																					)
																				}
																				className={
																					isFieldEmpty(indicator.responsibleAuthority)
																						? 'error-border'
																						: ''
																				}
																			/>
																		</Col>
																		<Col span={12}>
																			<Form.Item
																				label={t('kurultai.deadlineDate')}
																				rules={[
																					{
																						required: true,
																						message: t('msg.fill_in_the_field'),
																					},
																				]}
																			>
																				<DatePicker
																					format={dateFormat}
																					value={
																						indicator.deadlineDate
																							? dayjs(indicator.deadlineDate)
																							: undefined
																					}
																					onChange={date =>
																						handleIndicatorChange(
																							item.key as string,
																							task.key as string,
																							indicator.key as string,
																							'deadlineDate',
																							date
																						)
																					}
																					status={
																						isFieldEmpty(indicator.deadlineDate) ? 'error' : ''
																					}
																				/>
																			</Form.Item>
																		</Col>
																		<Col span={12}>
																			<label>{t('kurultai.eventDetails')}</label>
																			{/* Делаем поле многострочным и авторасширяемым */}
																			<Input.TextArea
																				autoSize={{ minRows: 2, maxRows: 6 }}
																				value={indicator.eventDetails || ''}
																				onChange={e =>
																					handleIndicatorChange(
																						item.key as string,
																						task.key as string,
																						indicator.key as string,
																						'eventDetails',
																						e.target.value
																					)
																				}
																				// eventDetails НЕ обязательное, поэтому без "error-border"
																			/>
																		</Col>
																		<Button
																			className='fixed_delete'
																			type='primary'
																			title='Удалить'
																			danger
																			icon={<CloseOutlined />}
																			shape='circle'
																			size='small'
																			onClick={() =>
																				handleDeleteIndicator(
																					item.key as string,
																					task.key as string,
																					indicator.key as string
																				)
																			}
																			disabled={(task.eventsList || []).length <= 1}
																		/>
																	</Row>
																</div>
															))}
															<Button
																style={{ marginTop: 15 }}
																type='primary'
																onClick={() =>
																	handleAddIndicator(item.key as string, task.key as string)
																}
															>
																{t('kurultai.add_indicator')}
															</Button>
														</Col>
													</Row>
												</div>
											</Row>
										</div>
									))}
									<Button
										style={{ marginTop: 15 }}
										type='primary'
										onClick={() => handleAddTask(item.key as string)}
									>
										{t('kurultai.add_task')}
									</Button>
								</Col>
							</Row>
						</div>
					</Tabs.TabPane>
				))}
			</Tabs>

			<Button
				style={{ marginTop: 15 }}
				icon={<PlusCircleOutlined />}
				type='primary'
				onClick={handleAddItem}
			>
				{t('kurultai.add_assignment')}
			</Button>
		</div>
	)
}
